// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useState, useCallback } from 'react'
import { CheckCircle2, Circle, ArrowRight } from 'lucide-react'
import { useNavigate }    from 'react-router-dom'
import { useWizardStore, useAuthStore } from '@/store'
import { Card }           from '@/components/ui/Card'
import { ROUTES, WIZARD_ROUTE } from '@/lib/constants/routes'
import { getAllModuleProgress } from '@/services/profile'
import { loadTrustedPersons }  from '@/services/trustedPerson'
import type { SaveStatus } from '@/hooks/useAutosave'

interface ReviewItem {
  label:   string
  done:    boolean
  route:   string
  section: string
  step?:   number
}

export function NewStep14Review({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const navigate     = useNavigate()
  const { answers }  = useWizardStore()
  const { userId }   = useAuthStore()
  const [hasVP, setHasVP]     = useState(false)
  const [modProgress, setModProgress] = useState<Map<string, string>>(new Map())

  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  const load = useCallback(async () => {
    if (!userId) return
    // Load trusted persons
    const vps = await loadTrustedPersons(userId)
    setHasVP(vps.filter(p => p.confirmationStatus !== 'REVOKED').length > 0)
    // Load module progress
    const records = await getAllModuleProgress(userId)
    const map = new Map(records.map(r => [r.moduleName, r.status]))
    setModProgress(map)
  }, [userId])

  useEffect(() => { load() }, [load])

  const isComplete = (moduleId: string) =>
    modProgress.get(moduleId) === 'COMPLETE' || modProgress.get(moduleId) === 'IN_PROGRESS'
  const extraLocsDone = isComplete('extraLocations')

  // Definitieve wizard-volgorde (0–22)
  const items: ReviewItem[] = [
    // Persoonlijk & netwerk
    { section: 'Persoonlijk & netwerk', label: 'Persoonlijke gegevens', done: isComplete('intake'),              route: WIZARD_ROUTE, step: 0 },
    { section: 'Persoonlijk & netwerk', label: 'Partner',               done: isComplete('partner'),             route: WIZARD_ROUTE, step: 1 },
    { section: 'Persoonlijk & netwerk', label: 'Vertrouwenspersoon',    done: hasVP || isComplete('trusted_person'), route: WIZARD_ROUTE, step: 2 },
    { section: 'Persoonlijk & netwerk', label: 'Kinderen & voogdij',    done: answers.hasChildren !== undefined,  route: WIZARD_ROUTE, step: 3 },
    { section: 'Persoonlijk & netwerk', label: 'Huisdieren',            done: answers.hasPets !== undefined,      route: WIZARD_ROUTE, step: 4 },
    // Bezittingen
    { section: 'Bezittingen',        label: 'Wonen',                  done: answers.hasOwnHome !== undefined,   route: WIZARD_ROUTE, step: 5 },
    { section: 'Bezittingen',        label: 'Tweede woning',          done: answers.hasSecondHome !== undefined, route: WIZARD_ROUTE, step: 6 },
    { section: 'Bezittingen',        label: 'Extra locaties',         done: extraLocsDone,                      route: WIZARD_ROUTE, step: 7 },
    { section: 'Bezittingen',        label: 'Onderneming',            done: answers.hasBusiness !== undefined,  route: WIZARD_ROUTE, step: 8 },
    { section: 'Bezittingen',        label: 'Voertuigen & mobiliteit', done: isComplete('vehicles'),            route: WIZARD_ROUTE, step: 9 },
    { section: 'Bezittingen',        label: 'Financieel',             done: isComplete('financial'),           route: WIZARD_ROUTE, step: 10 },
    { section: 'Bezittingen',        label: 'Aandelen & beleggingen', done: answers.hasInvestments !== undefined, route: WIZARD_ROUTE, step: 11 },
    // Persoonlijke wensen
    { section: 'Persoonlijke wensen', label: 'Donorregistratie',      done: !!answers.donorStatus,             route: WIZARD_ROUTE, step: 12 },
    { section: 'Persoonlijke wensen', label: 'Medische wensen',       done: isComplete('medical'),             route: WIZARD_ROUTE, step: 13 },
    { section: 'Persoonlijke wensen', label: 'Testament',             done: answers.hasTestament !== undefined, route: WIZARD_ROUTE, step: 14 },
    { section: 'Persoonlijke wensen', label: 'Levenstestament',       done: answers.hasLivingWill !== undefined, route: WIZARD_ROUTE, step: 15 },
    { section: 'Persoonlijke wensen', label: 'Uitvaartwensen',        done: isComplete('funeral'),             route: WIZARD_ROUTE, step: 16 },
    { section: 'Persoonlijke wensen', label: 'Uitvaartverzekering & kosten', done: isComplete('funeral'),       route: WIZARD_ROUTE, step: 17 },
    // Nalatenschap & afsluiting
    { section: 'Nalatenschap & afsluiting', label: 'Digitale nalatenschap', done: isComplete('digital_estate'), route: WIZARD_ROUTE, step: 18 },
    { section: 'Nalatenschap & afsluiting', label: 'Documentwijzer',  done: isComplete('documents'),           route: WIZARD_ROUTE, step: 19 },
    { section: 'Nalatenschap & afsluiting', label: 'Persoonlijk afscheid', done: isComplete('farewell'),        route: WIZARD_ROUTE, step: 20 },
    { section: 'Nalatenschap & afsluiting', label: 'Berichtenservice', done: isComplete('messages'),            route: WIZARD_ROUTE, step: 21 },
    { section: 'Nalatenschap & afsluiting', label: 'Notariële wensen', done: isComplete('notarial'),            route: ROUTES.NOTARIAL },
  ]


  // Groepeer per sectie
  const sections = Array.from(new Set(items.map(i => i.section)))

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Controle & afronding</h2>
        <p className="text-sm text-ink-3 leading-relaxed">
          Overzicht van alle onderdelen. Klik op een onderdeel om het verder in te vullen.
        </p>
      </div>

      {/* Per sectie */}
      {sections.map(section => (
        <Card key={section}>
          <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-3">{section}</p>
          <div className="flex flex-col gap-1.5">
            {items.filter(i => i.section === section).map((item, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (item.step !== undefined) { useWizardStore.getState().setStep(item.step); navigate(WIZARD_ROUTE) }
                  else navigate(item.route)
                }}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-sand-50 transition-colors"
              >
                {item.done
                  ? <CheckCircle2 className="h-5 w-5 text-sage-500 flex-shrink-0" />
                  : <Circle       className="h-5 w-5 text-sand-400 flex-shrink-0" />
                }
                <span className={`flex-1 text-sm ${item.done ? 'text-ink' : 'text-ink-3'}`}>
                  {item.label}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-ink-4 flex-shrink-0" />
              </button>
            ))}
          </div>
        </Card>
      ))}

      <p className="text-xs text-ink-4 text-center leading-relaxed">
        U kunt de wizard altijd sluiten en later verdergaan via het dashboard.
        Uw gegevens worden automatisch opgeslagen.
      </p>
    </div>
  )
}
